import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { useAnalytics } from "@/hooks/useAnalytics";
import RequireAuth from "@/components/RequireAuth";
import RequirePaid from "@/components/RequirePaid";
import RequireInstructor from "@/components/RequireInstructor";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Module from "@/pages/Module";
import Lesson from "@/pages/Lesson";
import Quiz from "@/pages/Quiz";
import FinalExamIntro from "@/pages/FinalExamIntro";
import Certificate from "@/pages/Certificate";
import VerifyCertificate from "@/pages/VerifyCertificate";
import Profile from "@/pages/Profile";
import S98Review from "@/pages/S98Review";
import Glossary from "@/pages/Glossary";
import SampleLesson from "@/pages/SampleLesson";
import Enroll from "@/pages/Enroll";
import EnrollSuccess from "@/pages/EnrollSuccess";
import Redeem from "@/pages/Redeem";
import GiftSuccess from "@/pages/GiftSuccess";
import InstructorDashboard from "@/pages/InstructorDashboard";
import InstructorStudent from "@/pages/InstructorStudent";
import InstructorEmailLogs from "@/pages/InstructorEmailLogs";
import InstructorCompCodes from "@/pages/InstructorCompCodes";
import Lab from "@/pages/Lab";
import RiserDiagrams from "@/pages/RiserDiagrams";
import PanelSimulatorPage from "@/pages/PanelSimulator";
import Funnel from "@/pages/Funnel";
import Tracks from "@/pages/Tracks";
import TrackDetail from "@/pages/TrackDetail";
import InstructorBio from "@/pages/InstructorBio";
import InstructorUnlock from "@/pages/InstructorUnlock";
import InstructorLeads from "@/pages/InstructorLeads";
import InstructorLaunch from "@/pages/InstructorLaunch";
import InstructorCorporate from "@/pages/InstructorCorporate";
import InstructorIntegrations from "@/pages/InstructorIntegrations";
import InstructorPlaybook from "@/pages/InstructorPlaybook";
import InstructorAIDrafts from "@/pages/InstructorAIDrafts";
import PitchDeck from "@/pages/PitchDeck";
import CheatSheet from "@/pages/CheatSheet";
import Top50Questions from "@/pages/Top50Questions";
import FreeSeminar from "@/pages/FreeSeminar";
import InstructorSeminars from "@/pages/InstructorSeminars";
import Transcript from "@/pages/Transcript";
import CareerRoadmap from "@/pages/CareerRoadmap";
import FreeGuide from "@/pages/FreeGuide";
import CorporateTraining from "@/pages/CorporateTraining";
import TrackPublic from "@/pages/TrackPublic";
import { ScrollToTop } from "@/components/ScrollToTop";
import NicetPrep from "@/pages/NicetPrep";

function AnalyticsListener() {
  const loc = useLocation();
  useAnalytics(loc.pathname);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsListener />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pitch-deck" element={<PitchDeck />} />
            <Route path="/cheat-sheet" element={<CheatSheet />} />
            <Route path="/top-50-questions" element={<Top50Questions />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />

            {/* Auth-required but FREE */}
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/transcript" element={<RequireAuth><Transcript /></RequireAuth>} />
            <Route path="/enroll" element={<RequireAuth><Enroll /></RequireAuth>} />
            <Route path="/enroll/success" element={<RequireAuth><EnrollSuccess /></RequireAuth>} />
            <Route path="/redeem" element={<RequireAuth><Redeem /></RequireAuth>} />
            <Route path="/gift/success" element={<RequireAuth><GiftSuccess /></RequireAuth>}/>

            {/* Paid-only routes */}
            <Route path="/module/:moduleId" element={<RequirePaid><Module /></RequirePaid>} />
            <Route path="/lesson/:lessonId" element={<RequirePaid><Lesson /></RequirePaid>} />
            <Route path="/quiz/:quizId" element={<RequirePaid><Quiz /></RequirePaid>} />
            <Route path="/final-exam" element={<RequirePaid><FinalExamIntro /></RequirePaid>} />
            <Route path="/certificate" element={<RequirePaid><Certificate /></RequirePaid>} />
            <Route path="/s98-review" element={<RequirePaid><S98Review /></RequirePaid>} />
            <Route path="/glossary" element={<RequirePaid><Glossary /></RequirePaid>}/>
            <Route path="/lab" element={<RequirePaid><Lab /></RequirePaid>}/>
            <Route path="/lab/risers" element={<RequirePaid><RiserDiagrams /></RequirePaid>}/>
            <Route path="/lab/panel" element={<RequirePaid><PanelSimulatorPage /></RequirePaid>}/>
            <Route path="/tracks" element={<RequirePaid><Tracks /></RequirePaid>}/>
            <Route path="/track/:trackId" element={<TrackPublic />}/>
            <Route path="/learn/track/:trackId" element={<RequirePaid><TrackDetail /></RequirePaid>}/>

            {/* Public */}
            <Route path="/sample" element={<SampleLesson />} />
            <Route path="/start" element={<Funnel />} />
            <Route path="/about-instructor" element={<InstructorBio />} />
            <Route path="/career" element={<CareerRoadmap />} />
            <Route path="/career-roadmap" element={<CareerRoadmap />} />
            <Route path="/free-guide" element={<FreeGuide />} />
            <Route path="/free-seminar" element={<FreeSeminar />} />
            <Route path="/seminar" element={<FreeSeminar />} />
            <Route path="/corporate" element={<CorporateTraining />} />
            <Route path="/corporate-training" element={<CorporateTraining />} />
            <Route path="/nicet-exam-prep" element={<NicetPrep />} />

            {/* Public certificate verification (QR target) */}
            <Route path="/verify/:certId" element={<VerifyCertificate />} />

            {/* Instructor-only */}
            <Route path="/instructor" element={<RequireInstructor><InstructorDashboard /></RequireInstructor>} />
            <Route path="/instructor/student/:id" element={<RequireInstructor><InstructorStudent /></RequireInstructor>} />
            <Route path="/instructor/email-logs" element={<RequireInstructor><InstructorEmailLogs /></RequireInstructor>} />
            <Route path="/instructor/comp-codes" element={<RequireInstructor><InstructorCompCodes /></RequireInstructor>} />
            <Route path="/instructor/unlock" element={<RequireInstructor><InstructorUnlock /></RequireInstructor>} />
            <Route path="/instructor/leads" element={<RequireInstructor><InstructorLeads /></RequireInstructor>} />
            <Route path="/instructor/launch" element={<RequireInstructor><InstructorLaunch /></RequireInstructor>} />
            <Route path="/instructor/corporate" element={<RequireInstructor><InstructorCorporate /></RequireInstructor>} />
            <Route path="/instructor/integrations" element={<RequireInstructor><InstructorIntegrations /></RequireInstructor>} />
            <Route path="/instructor/playbook" element={<RequireInstructor><InstructorPlaybook /></RequireInstructor>} />
            <Route path="/instructor/ai-drafts" element={<RequireInstructor><InstructorAIDrafts /></RequireInstructor>} />
            <Route path="/instructor/seminars" element={<RequireInstructor><InstructorSeminars /></RequireInstructor>} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
